import React from 'react';

import { cn } from 'utils/cn';

export interface LegacyTextProps<T extends React.ElementType> {
  as?: T;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type PropTypes<T extends React.ElementType> = LegacyTextProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof LegacyTextProps<T>>;

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example <Text>{children}</Text>
 */
export const Body = React.forwardRef(
  <T extends React.ElementType = 'p'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLParagraphElement>,
  ) => {
    const Component = as || 'p';
    return (
      <Component
        ref={ref}
        className={cn(
          'font-body text-body-regular font-regular leading-tight',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example <Text variant="bodySemibold">{children}</Text>
 */
export const BodyStrong = React.forwardRef(
  <T extends React.ElementType = 'p'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLParagraphElement>,
  ) => {
    const Component = as || 'p';
    return (
      <Component
        ref={ref}
        className={cn(
          'font-body text-body-regular font-semibold leading-tight',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example <Text variant="headerExtraLarge">{children}</Text>
 */
export const Headline = React.forwardRef(
  <T extends React.ElementType = 'h1'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLHeadingElement>,
  ) => {
    const Component = as || 'h1';
    return (
      <Component
        ref={ref}
        className={cn(
          'font-heading text-header-extra-large font-bold leading-tight tracking-tight',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example <Text variant="headerLarge">{children}</Text>
 */
export const Subtitle = React.forwardRef(
  <T extends React.ElementType = 'h2'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLHeadingElement>,
  ) => {
    const Component = as || 'h2';
    return (
      <Component
        ref={ref}
        className={cn(
          'font-heading text-header-regular font-semibold leading-tight tracking-tight',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example <Text variant="headerRegular">{children}</Text>
 */
export const Title = React.forwardRef(
  <T extends React.ElementType = 'h3'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLHeadingElement>,
  ) => {
    const Component = as || 'h3';
    return (
      <Component
        ref={ref}
        className={cn(
          'font-heading text-header-regular font-semibold leading-tight tracking-tight',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example
 * <Text as="p" textTransform="uppercase" variant="bodySmallSemibold">
 *   {children}
 * </Text>
 */
export const InputText = React.forwardRef(
  <T extends React.ElementType = 'p'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLParagraphElement>,
  ) => {
    const Component = as || 'p';
    return (
      <Component
        ref={ref}
        className={cn(
          'font-body text-body-small font-semibold leading-tight uppercase tracking-normal',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example
 * <Text as="p" variant="bodySmall">
 *   {children}
 * </Text>
 */
export const Caption = React.forwardRef(
  <T extends React.ElementType = 'p'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLParagraphElement>,
  ) => {
    const Component = as || 'p';
    return (
      <Component
        ref={ref}
        className={cn(
          'text-body-small font-regular leading-tight tracking-normal',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example
 * <Text as="p" variant="bodySmallSemibold">
 *   {children}
 * </Text>
 */
export const CaptionStrong = React.forwardRef(
  <T extends React.ElementType = 'p'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLParagraphElement>,
  ) => {
    const Component = as || 'p';
    return (
      <Component
        ref={ref}
        className={cn(
          'text-body-small font-semibold leading-tight tracking-normal',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);

/**
 * @deprecated This component is deprecated in favor of the atoms/Text
 * @example
 * <Text as="p" lineHeight="exact" variant="bodySemibold">
 *   {children}
 * </Text>
 */
export const MenuLabel = React.forwardRef(
  <T extends React.ElementType = 'p'>(
    { as, className, ...props }: PropTypes<T>,
    ref: React.Ref<HTMLParagraphElement>,
  ) => {
    const Component = as || 'p';
    return (
      <Component
        ref={ref}
        className={cn(
          'font-body text-body-regular font-semibold leading-exact',
          className,
        )}
        {...(props as any)}
      />
    );
  },
);
